export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '—'
  const seconds = Math.round((Date.now() - Date.parse(iso)) / 1000)
  if (seconds < 5) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours}h ago`
  return new Date(iso).toLocaleDateString()
}

/** How long until a moment still ahead: `in 14 days`. Past moments read `expired`. */
export function timeUntil(iso: string | null | undefined): string {
  if (!iso) return '—'
  const seconds = Math.round((Date.parse(iso) - Date.now()) / 1000)
  if (seconds <= 0) return 'expired'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `in ${Math.max(minutes, 1)} min`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `in ${hours}h`
  return `in ${Math.round(hours / 24)} days`
}

export function elapsed(from: string | null | undefined, to: string | null | undefined): string {
  if (!from) return '—'
  const ms = (to ? Date.parse(to) : Date.now()) - Date.parse(from)
  if (ms < 1000) return `${Math.max(ms, 0)} ms`
  const seconds = ms / 1000
  if (seconds < 60) return `${seconds.toFixed(1)} s`
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
}

export function groupBy<T>(items: readonly T[], key: (item: T) => string): [string, T[]][] {
  const groups = new Map<string, T[]>()
  for (const item of items) {
    const name = key(item)
    const list = groups.get(name)
    if (list) list.push(item)
    else groups.set(name, [item])
  }
  return [...groups.entries()]
}

export function shortId(id: string): string {
  return id.slice(0, 8)
}

export function prettyJson(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2)
  } catch {
    return text
  }
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—'
  const total = Math.round(ms / 1000)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

/**
 * Credits from micro-credits (1 credit = 1,000,000): balances round down,
 * estimates up, and ledger amounts show up to two decimals.
 */
export function formatCredits(micro: number, round: 'down' | 'up' | 'exact' = 'down'): string {
  const credits = micro / 1_000_000
  if (round === 'exact') return credits.toLocaleString('en-US', { maximumFractionDigits: 2 })
  return (round === 'up' ? Math.ceil(credits) : Math.floor(credits)).toLocaleString('en-US')
}

/** Dollars from micro-dollars; amounts under a dollar keep four places so a few cents of spend still show. */
export function formatMicroDollars(micros: number): string {
  const dollars = Math.abs(micros) / 1_000_000
  const places = micros !== 0 && dollars < 1 ? 4 : 2
  return `${micros < 0 ? '−' : ''}$${dollars.toLocaleString('en-US', { minimumFractionDigits: places, maximumFractionDigits: places })}`
}

/** A date as people read it; with `time`, the hour too. */
export function formatDate(iso: string, time = false): string {
  const date = new Date(iso)
  return time
    ? date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let value = bytes
  let unit = 0
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000
    unit++
  }
  return `${value >= 10 || unit === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unit]}`
}

export function titleFromFilename(name: string): string {
  const base = name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
  return base ? base.charAt(0).toUpperCase() + base.slice(1) : 'Untitled episode'
}

/** A day as `Sep 15`, read as the UTC day it names rather than the browser's. */
export function shortDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/** A count for a label: `1,204`. */
export function formatCount(value: number): string {
  return value.toLocaleString('en-US')
}

/** A language tag as its name in the reader's own language: `es` is Spanish. */
export function languageName(tag: string | null | undefined): string {
  if (!tag) return 'Language not detected'
  try {
    return new Intl.DisplayNames(undefined, { type: 'language' }).of(tag) ?? tag
  } catch {
    return tag
  }
}
